import { useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ConcertCard } from "../components/ConcertCard";
import { Reveal, Eyebrow } from "../components/Reveal";
import { monthLong } from "../lib/format";

const unique = (arr) => [...new Set(arr)];

export const Lineup = ({ concerts, loading }) => {
  const upcoming = concerts.filter((c) => !c.is_past);
  const [month, setMonth] = useState("All");
  const [genre, setGenre] = useState("All");
  const months = useMemo(() => unique(upcoming.map((c) => monthLong(c.date))), [upcoming]);
  const genres = useMemo(() => unique(upcoming.map((c) => c.genre)).sort(), [upcoming]);
  const shown = upcoming.filter((c) => (month === "All" || monthLong(c.date) === month) && (genre === "All" || c.genre === genre));

  return (
    <section id="lineup" data-testid="lineup-section" className="mx-auto max-w-7xl px-5 py-20 lg:px-12 lg:py-28">
      <Reveal className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Eyebrow>The Summer Lineup</Eyebrow>
          <h2 className="mt-3 font-display text-5xl font-extrabold uppercase leading-[0.9] text-white sm:text-7xl">Every night <br className="hidden sm:block" /><span className="text-gradient">a headliner.</span></h2>
        </div>
        <p className="max-w-sm text-zinc-300">Browse upcoming shows, filter by month or genre, and grab tickets before they're gone.</p>
      </Reveal>

      <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div role="group" aria-label="Filter by month" className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 md:mx-0 md:px-0">
          {["All", ...months].map((m) => (
            <button key={m} data-testid={`filter-month-${m.toLowerCase()}`} aria-pressed={month === m} onClick={() => setMonth(m)} className={`chip ${month === m ? "chip-active" : ""}`}>
              {m === "All" ? "All Months" : m}
            </button>
          ))}
        </div>
        <Select value={genre} onValueChange={setGenre}>
          <SelectTrigger data-testid="filter-genre-select" aria-label="Filter by genre" className="h-11 w-full rounded-full border-white/15 bg-[#12141C] px-5 font-mono text-xs uppercase tracking-widest text-white md:w-60">
            <SelectValue placeholder="All Genres" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#12141C] text-white">
            <SelectItem value="All" data-testid="genre-option-all">All Genres</SelectItem>
            {genres.map((g) => <SelectItem key={g} value={g} data-testid={`genre-option-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <p data-testid="lineup-result-count" className="mb-6 font-mono text-xs uppercase tracking-widest text-zinc-400">{loading ? "Loading shows…" : `${shown.length} upcoming ${shown.length === 1 ? "show" : "shows"}`}</p>

      {shown.length > 0 ? (
        <div data-testid="lineup-grid" className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {shown.map((c, i) => <ConcertCard key={c.slug} concert={c} index={i} />)}
        </div>
      ) : !loading && (
        <div data-testid="lineup-empty" className="rounded-[22px] border border-dashed border-white/15 p-12 text-center text-zinc-300">
          No shows match these filters. <button data-testid="lineup-reset-filters" className="text-sunset underline" onClick={() => { setMonth("All"); setGenre("All"); }}>Reset filters</button>
        </div>
      )}
    </section>
  );
};
